import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FiList } from 'react-icons/fi';
import Sidebar from '../components/Sidebar';
import BreadcrumbBack from '../components/BreadcrumbBack';
import VideoPreview from '../components/clip-editor/VideoPreview';
import CaptionPanel from '../components/clip-editor/CaptionPanel';
import Timeline from '../components/clip-editor/Timeline';
import TimelineToolbar from '../components/clip-editor/TimelineToolbar';
import Toolbar from '../components/clip-editor/Toolbar';
import ExportModal from '../components/clip-editor/ExportModal';
import SegmentContextMenu from '../components/clip-editor/SegmentContextMenu';
import AdjustPanel from '../components/clip-editor/AdjustPanel';
import SegmentToolbar from '../components/clip-editor/SegmentToolbar';
import CropOverlay from '../components/clip-editor/CropOverlay';
import EmojiPicker from '../components/clip-editor/EmojiPicker';
import DraggableOverlay from '../components/clip-editor/DraggableOverlay';
import ImageOverlayPanel from '../components/clip-editor/ImageOverlayPanel';
import { ImageCrop } from '../components/clip-editor/ImageCropBox';
import ThumbnailPicker from '../components/clip-editor/ThumbnailPicker';
import FiltersPanel, { FilterState, buildFilterCSS } from '../components/clip-editor/FiltersPanel';
import MusicPanel, { MusicTrack } from '../components/clip-editor/MusicPanel';

const RailIcon = FiList as unknown as React.FC<{ 'aria-hidden'?: boolean }>;

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface Caption {
  id: string;
  text: string;
  start_time: number;
  end_time: number;
  style: Record<string, any>;
}

interface Segment {
  id: string;
  index: number;
  startTime: number;
  endTime: number;
  /**
   * The segment's own level and speed. `null` means it carries none and takes the clip's
   * master volume / playback speed — the same fallback the render resolves, so a clip-wide
   * change reaches every segment the author never set individually.
   */
  volume: number | null;
  muted: boolean;
  fadeIn: number;
  fadeOut: number;
  speed: number | null;
  transition: string;
}

interface Sibling {
  id: string;
  index: number;
  title: string;
  score: number;
  duration: number;
  file_url: string;
}

interface CropData {
  x: number; y: number; w: number; h: number;
}

interface Overlay {
  id: string;
  type: string;
  content: string;
  startTime: number;
  endTime: number;
  x: number;
  y: number;
  scale: number;
}

/** A freshly added overlay shows all of its source until the author crops it. */
const FULL_CROP: ImageCrop = { x: 0, y: 0, w: 1, h: 1 };

/** Turns a stored asset reference into something the browser can load. */
const assetSrc = (url: string) =>
  !url || url.startsWith('http') ? url : `${API_BASE}${url}`;

interface ClipImage {
  id: string;
  layer: string;
  /** The stored key — what gets saved back. Never load this directly. */
  imageUrl: string;
  /** A URL the browser can load, presigned and short-lived. */
  src: string;
  x: number;
  y: number;
  width: number;
  height: number;
  opacity: number;
  rotation: number;
  startTime: number;
  endTime: number | null;
  /** Segments this image is tied to; its on-screen range is derived from their bounds. */
  segmentIds: string[];
  /**
   * Non-destructive crop on the source image, as fractions of it. It changes what is drawn
   * inside the overlay's box, never the box itself — so cropping never moves or resizes it.
   */
  crop: ImageCrop;
}

interface ClipData {
  id: string;
  videoId: string;
  projectId: string | null;
  index: number;
  title: string;
  caption: string;
  tags: string[];
  score: number;
  startTime: number;
  endTime: number;
  duration: number;
  fileUrl: string;
  previewIsSource?: boolean;
  sourceVideoUrl: string;
  platform: string;
  captions: Caption[];
  segments: Segment[];
  overlays: any[];
  images: any[];
  thumbnailUrl: string | null;
  /** Loadable URL for the thumbnail; the editor only displays it, never saves it back. */
  thumbnailSrc?: string | null;
  thumbnailFrames: string[];
  aspectRatio: string;
  editVersion: number;
  crop: CropData;
  masterVolume: number;
  playbackSpeed: number;
  filterPreset: string;
  filterBrightness: number;
  filterContrast: number;
  filterSaturation: number;
  originalAudioVolume: number;
  music: MusicTrack | null;
  siblings: Sibling[];
}

// Undo state snapshot
interface EditorSnapshot {
  captions: Caption[];
  segments: Segment[];
  aspectRatio: string;
  masterVolume: number;
  playbackSpeed: number;
}

/**
 * Local segment ids, minted per video. Segments created in the editor have no database row
 * yet, so they carry an id generated here — scoped by video, monotonic within the session, so
 * two segments made in the same millisecond cannot collide. The `seg-` prefix is what
 * `handleSave` reads to tell a local id from a persisted one.
 */
let segIdSeq = 0;
const makeSegmentId = (videoScope: string) => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Older browsers: a v4-shaped id built from the scope, the clock and a counter.
  const rand = () => Math.floor(Math.random() * 0x10000).toString(16).padStart(4, '0');
  const seq = ((Date.now() + segIdSeq++) % 0xffff).toString(16).padStart(4, '0');
  void videoScope;
  return `${rand()}${rand()}-${rand()}-4${rand().slice(1)}-8${seq.slice(1)}-${rand()}${rand()}${rand()}`;
};

const ClipEditor: React.FC = () => {
  const { shortId, projectId, videoId } = useParams<{ shortId: string; projectId?: string; videoId?: string }>();
  const navigate = useNavigate();
  // Opened from a Story: the author came from a slide and expects to land back on it.
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const storyId = searchParams.get('storyId');
  const slideParam = searchParams.get('slide');
  // The renders viewer sends the author here to make a new version, so Done runs the export.
  const exportOnDone = searchParams.get('exportOnDone') === '1';
  // What to call the page the author came from, so a non-story return reads as more than "back".
  const returnLabel = searchParams.get('returnLabel');
  const [storyCtx, setStoryCtx] = useState<{ name: string; projectId: string | null; projectName: string } | null>(null);

  useEffect(() => {
    if (!storyId) { setStoryCtx(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/image-projects/${storyId}`, { credentials: 'include' });
        if (!res.ok) return;
        const story = await res.json();
        let projectName = 'Project';
        if (story.projectId) {
          const pRes = await fetch(`${API_BASE}/api/projects/${story.projectId}`, { credentials: 'include' });
          if (pRes.ok) projectName = (await pRes.json()).name || projectName;
        }
        if (!cancelled) setStoryCtx({ name: story.name, projectId: story.projectId, projectName });
      } catch { /* breadcrumb falls back to the clip title */ }
    })();
    return () => { cancelled = true; };
  }, [storyId]);
  const [clipData, setClipData] = useState<ClipData | null>(null);
  /** Bumped by Discard, to reload the clip as the server holds it. */
  const [reloadNonce, setReloadNonce] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Editor state
  const [captions, setCaptions] = useState<Caption[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [aspectRatio, setAspectRatio] = useState('9:16');
  const [masterVolume, setMasterVolume] = useState(1.0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [crop, setCrop] = useState<CropData>({ x: 0, y: 0, w: 1, h: 1 });
  const [currentTime, setCurrentTime] = useState(0);
  // Caption at the playhead, or the last one passed, for the phone "now playing" strip.
  const nowCaption = useMemo(() => {
    let last: Caption | null = null;
    for (const c of captions) {
      if (currentTime >= c.start_time && currentTime < c.end_time) return c;
      if (c.start_time <= currentTime) last = c;
    }
    return last;
  }, [captions, currentTime]);
  const [duration, setDuration] = useState(0);
  /**
   * The selected segment, by id. A reorder renumbers every segment, so an index would leave the
   * selection pointing at whichever segment slid into that slot — the toolbar would then act on
   * a segment the author never picked. `segPlay` is keyed the same way, for the same reason.
   */
  const [selectedSegment, setSelectedSegment] = useState<string | null>(null);
  const [showExport, setShowExport] = useState(false);
  const [rightTab, setRightTab] = useState<'tool' | 'caption'>('tool');
  // Below 900px the Tool | Caption rail is a left-hand overlay drawer (see responsive.css).
  const [railOpen, setRailOpen] = useState(false);
  useEffect(() => {
    if (!railOpen) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setRailOpen(false); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [railOpen]);
  const [dirty, setDirty] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [trimActive, setTrimActive] = useState(false);
  // At most two segments may be selected, and only if they are neighbours — the second is
  // what Merge folds into the first.
  const [secondarySegment, setSecondarySegment] = useState<string | null>(null);
  // Which segment the preview is playing on its own, and whether it repeats.
  // Keyed by segment id, not index: a split, merge or reorder renumbers segments, and an
  // index would leave playback running against whatever slid into that slot.
  const [segPlay, setSegPlay] = useState<{ id: string; loop: boolean } | null>(null);
  const [segPlayNonce, setSegPlayNonce] = useState(0);
  const [showAdjust, setShowAdjust] = useState(false);

  // v3 state
  const [overlays, setOverlays] = useState<Overlay[]>([]);
  const [images, setImages] = useState<ClipImage[]>([]);
  const [filters, setFilters] = useState<FilterState>({ preset: 'normal', brightness: 0, contrast: 0, saturation: 0 });
  const [music, setMusic] = useState<MusicTrack | null>(null);
  const [originalAudioVolume, setOriginalAudioVolume] = useState(1.0);
  const [cropMode, setCropMode] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showMusic, setShowMusic] = useState(false);
  const [showThumbnail, setShowThumbnail] = useState(false);
  const [selectedOverlayId, setSelectedOverlayId] = useState<string | null>(null);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  // The overlay tray is opened by selecting an image and stays open until it is closed, so
  // clicking away from the image on the frame does not take the controls with it.
  const [imagePanelId, setImagePanelId] = useState<string | null>(null);
  /** The image whose crop is being dragged on the frame, if any. */
  const [croppingImageId, setCroppingImageId] = useState<string | null>(null);
  const [exportSegment, setExportSegment] = useState<{ start: number; end: number; label: string } | null>(null);

  // Context menu
  // `atTime` is where in the segment the author right-clicked — the cut a Split Here makes.
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; segIndex: number; atTime: number } | null>(null);

  // Undo/redo
  const [undoStack, setUndoStack] = useState<EditorSnapshot[]>([]);
  const [redoStack, setRedoStack] = useState<EditorSnapshot[]>([]);

  const getSnapshot = useCallback((): EditorSnapshot => ({
    captions, segments, aspectRatio, masterVolume, playbackSpeed,
  }), [captions, segments, aspectRatio, masterVolume, playbackSpeed]);

  const pushUndo = useCallback(() => {
    setUndoStack(prev => [...prev.slice(-49), getSnapshot()]);
    setRedoStack([]);
    setDirty(true);
  }, [getSnapshot]);

  /**
   * One undo step for one drag. A drag handle fires its callback on every mouse move, so pushing
   * a snapshot per call would spend the whole 50-deep history on a single resize.
   */
  const gestureUndoRef = useRef(false);
  const pushUndoOnce = useCallback(() => {
    if (gestureUndoRef.current) return;
    gestureUndoRef.current = true;
    pushUndo();
    const end = () => { gestureUndoRef.current = false; window.removeEventListener('mouseup', end); };
    window.addEventListener('mouseup', end);
  }, [pushUndo]);

  // Resolve "first" shortId by fetching the video's shorts
  useEffect(() => {
    if (shortId !== 'first' || !videoId) return;
    fetch(`${API_BASE}/api/shorts/${videoId}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then((shorts: any[]) => {
        if (shorts.length > 0) {
          const firstId = shorts[0].id;
          if (projectId) {
            navigate(`/projects/${projectId}/video/${videoId}/clip/${firstId}`, { replace: true });
          } else {
            navigate(`/clips/${firstId}`, { replace: true });
          }
        } else {
          setError('No clips found for this video');
          setLoading(false);
        }
      })
      .catch(() => { setError('Failed to load clips'); setLoading(false); });
  }, [shortId, videoId, projectId, navigate]);

  // Load clip data
  useEffect(() => {
    if (!shortId || shortId === 'first') return;
    setLoading(true);
    fetch(`${API_BASE}/api/shorts/${shortId}/edit`, { credentials: 'include' })
      .then(r => {
        if (!r.ok) throw new Error('Failed to load clip data');
        return r.json();
      })
      .then((data: ClipData) => {
        setClipData({ ...data, thumbnailUrl: data.thumbnailSrc || data.thumbnailUrl });
        setCaptions(data.captions);
        setSegments(data.segments);
        setAspectRatio(data.aspectRatio);
        setMasterVolume(data.masterVolume);
        setPlaybackSpeed(data.playbackSpeed ?? 1.0);
        setCrop(data.crop);
        setDuration(data.duration);
        // v3 fields
        setOverlays((data.overlays || []).map((o: any) => ({
          id: o.id, type: o.type, content: o.content,
          startTime: o.start_time, endTime: o.end_time,
          x: o.x, y: o.y, scale: o.scale ?? 1.0,
        })));
        setImages((data.images || []).map((img: any) => ({
          id: img.id, layer: img.layer, imageUrl: img.image_url,
          src: assetSrc(img.src || img.image_url),
          x: img.x, y: img.y, width: img.width, height: img.height,
          opacity: img.opacity, rotation: img.rotation,
          startTime: img.start_time, endTime: img.end_time,
          segmentIds: img.segment_ids || [],
          crop: {
            x: img.crop_x ?? 0, y: img.crop_y ?? 0,
            w: img.crop_w ?? 1, h: img.crop_h ?? 1,
          },
        })));
        setFilters({
          preset: data.filterPreset || 'normal',
          brightness: data.filterBrightness ?? 0,
          contrast: data.filterContrast ?? 0,
          saturation: data.filterSaturation ?? 0,
        });
        setMusic(data.music || null);
        setOriginalAudioVolume(data.originalAudioVolume ?? 1.0);
        setUndoStack([]);
        setRedoStack([]);
        setDirty(false);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [shortId, reloadNonce]);

  /** Scopes locally-minted segment ids to this video. */
  const segIdScope = clipData?.videoId || shortId || 'clip';

  /** Read by the keyboard handler, which is registered once per selection, not per edit. */
  const segmentsRef = useRef<Segment[]>([]);
  useEffect(() => { segmentsRef.current = segments; }, [segments]);

  // A clip's stored duration can disagree with the media the editor actually draws — a story
  // clip carries the source's duration, not the rendered file's. Segments generated against the
  // stored value then sit off the end of the timeline, and every guard that compares the playhead
  // to a segment bound is measuring against a different length. Rebuild them against the real one.
  const segmentsRepairedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!clipData || duration <= 0) return;
    if (segmentsRepairedFor.current === `${clipData.id}:${reloadNonce}`) return;
    segmentsRepairedFor.current = `${clipData.id}:${reloadNonce}`;
    const EPS = 0.05;
    const fits = segments.length > 0 && segments.every(sg =>
      sg.endTime > sg.startTime && sg.startTime >= -EPS && sg.endTime <= duration + EPS
    );
    if (fits) return;
    const count = 4;
    const segLen = duration / count;
    setSegments(Array.from({ length: count }, (_, i) => ({
      id: makeSegmentId(clipData.videoId || clipData.id),
      index: i,
      startTime: i * segLen,
      endTime: Math.min((i + 1) * segLen, duration),
      volume: null,
      muted: false,
      fadeIn: 0,
      fadeOut: 0,
      speed: null,
      transition: 'none',
    })));
    setSelectedSegment(null);
    setSecondarySegment(null);
  }, [clipData, duration, segments, reloadNonce]);

  // An image bound to segments takes its range from them, so a split, merge, trim or reorder
  // carries the image with the picture instead of leaving it on a timestamp that no longer
  // means anything. Ids that no longer exist are dropped from the binding.
  useEffect(() => {
    if (segments.length === 0) return;
    setImages(prev => {
      let changed = false;
      const next = prev.map(img => {
        if (!img.segmentIds || img.segmentIds.length === 0) return img;
        const live = img.segmentIds.filter(id => segments.some(sg => sg.id === id));
        const picked = segments.filter(sg => live.includes(sg.id));
        const lostIds = live.length !== img.segmentIds.length;
        if (picked.length === 0) {
          if (!lostIds) return img;
          changed = true;
          return { ...img, segmentIds: live };
        }
        const startTime = Math.min(...picked.map(sg => sg.startTime));
        const endTime = Math.max(...picked.map(sg => sg.endTime));
        if (!lostIds && startTime === img.startTime && endTime === img.endTime) return img;
        changed = true;
        return { ...img, segmentIds: live, startTime, endTime };
      });
      return changed ? next : prev;
    });
  }, [segments]);

  // ── Segment handlers ──

  /** Mirrors the Timeline's drag clamp, so both paths agree on the shortest allowed segment. */
  const MIN_SEGMENT_DURATION = 0.5;

  const handleSegmentTrim = useCallback((segIndex: number, edge: 'start' | 'end', newTime: number) => {
    setSegments(prev => {
      const pos = prev.findIndex(s => s.index === segIndex);
      if (pos === -1) return prev;
      const cur = prev[pos];
      const before = prev[pos - 1];
      const after = prev[pos + 1];

      // Clamp against the neighbours so an edge can never cross them. Split and delete both
      // keep the list chronological; trim has to as well, or segments overlap and reorder.
      let t = newTime;
      if (edge === 'start') {
        const lower = before ? before.endTime : 0;
        t = Math.max(lower, Math.min(cur.endTime - MIN_SEGMENT_DURATION, t));
      } else {
        const upper = after ? after.startTime : Infinity;
        t = Math.min(upper, Math.max(cur.startTime + MIN_SEGMENT_DURATION, t));
      }
      if (t === (edge === 'start' ? cur.startTime : cur.endTime)) return prev;

      const next = prev.map(s => s.index === segIndex
        ? { ...s, [edge === 'start' ? 'startTime' : 'endTime']: t }
        : s);
      next.sort((a, b) => a.startTime - b.startTime);
      return next.map((s, i) => ({ ...s, index: i }));
    });
    setDirty(true);
  }, []);

  const handleSegmentReorder = useCallback((fromIndex: number, toIndex: number) => {
    pushUndo();
    setSegments(prev => {
      const arr = [...prev];
      const [moved] = arr.splice(fromIndex, 1);
      arr.splice(toIndex, 0, moved);
      return arr.map((s, i) => ({ ...s, index: i }));
    });
  }, [pushUndo]);

  /**
   * Moves a segment one place earlier or later — the keyboard-and-menu equivalent of dragging
   * it. A no-op at the ends, where the callers already disable the control.
   */
  const handleSegmentMove = useCallback((segIndex: number, direction: -1 | 1) => {
    const target = segIndex + direction;
    if (target < 0 || target >= segments.length) return;
    pushUndo();
    setSegments(prev => {
      const arr = [...prev];
      const [moved] = arr.splice(segIndex, 1);
      arr.splice(target, 0, moved);
      return arr.map((s, i) => ({ ...s, index: i }));
    });
    // Selection travels with the segment — by id, so it is still the same one after the shuffle.
    setSelectedSegment(segments[segIndex]?.id ?? null);
    setSecondarySegment(null);
  }, [segments, pushUndo]);

  const handleSegmentDelete = useCallback((segIndex: number) => {
    if (segments.length <= 1) return;
    pushUndo();
    setSegments(prev =>
      prev.filter(s => s.index !== segIndex).map((s, i) => ({ ...s, index: i }))
    );
    setSelectedSegment(null);
    setSecondarySegment(null);
  }, [segments.length, pushUndo]);

  // Rebuilds the four equal parts the server generates when a clip has no stored segments,
  // in clip time. The escape hatch for a segment list that has gone bad.
  const handleResetSegments = useCallback(() => {
    if (duration <= 0) return;
    pushUndo();
    const count = 4;
    const segLen = duration / count;
    setSegments(Array.from({ length: count }, (_, i) => ({
      id: makeSegmentId(segIdScope),
      index: i,
      startTime: i * segLen,
      endTime: Math.min((i + 1) * segLen, duration),
      volume: null,
      muted: false,
      fadeIn: 0,
      fadeOut: 0,
      speed: null,
      transition: 'none',
    })));
    setSelectedSegment(null);
    setSecondarySegment(null);
    setSegPlay(null);
    setDirty(true);
  }, [duration, pushUndo, segIdScope]);

  const handleSegmentSelect = useCallback((segIndex: number, additive: boolean) => {
    const seg = segments[segIndex];
    if (!seg) return;
    setSelectedSegment(prev => {
      const prevPos = prev !== null ? segments.findIndex(s => s.id === prev) : -1;
      // Merge only ever folds neighbours together, so a second pick is kept only when it is one.
      if (additive && prevPos >= 0 && Math.abs(segIndex - prevPos) === 1) {
        setSecondarySegment(seg.id);
        return prev;
      }
      setSecondarySegment(null);
      return seg.id;
    });
  }, [segments]);

  /** Folds the second selected segment into the first: one segment spanning both. */
  const handleSegmentMerge = useCallback(() => {
    if (selectedSegment === null || secondarySegment === null) return;
    const posA = segments.findIndex(s => s.id === selectedSegment);
    const posB = segments.findIndex(s => s.id === secondarySegment);
    if (posA < 0 || posB < 0) return;
    const lo = Math.min(posA, posB);
    const hi = Math.max(posA, posB);
    if (hi - lo !== 1) return;
    const a = segments[lo];
    const b = segments[hi];
    pushUndo();
    // The head segment's settings win; only the tail's end and fade-out carry over.
    const merged: Segment = {
      ...a,
      startTime: Math.min(a.startTime, b.startTime),
      endTime: Math.max(a.endTime, b.endTime),
      fadeOut: b.fadeOut,
    };
    setSegments(prev => {
      const arr = prev.filter(s => s.id !== a.id && s.id !== b.id);
      arr.push(merged);
      arr.sort((x, y) => x.startTime - y.startTime);
      return arr.map((s, i) => ({ ...s, index: i }));
    });
    setSelectedSegment(merged.id);
    setSecondarySegment(null);
  }, [selectedSegment, secondarySegment, segments, pushUndo, segIdScope]);

  const handleSegmentPlay = useCallback((segIndex: number) => {
    const seg = segments.find(sg => sg.index === segIndex);
    if (!seg) return;
    setSelectedSegment(seg.id);
    setSecondarySegment(null);
    setSegPlay(prev => (prev && prev.id === seg.id && !prev.loop) ? null : { id: seg.id, loop: false });
    setSegPlayNonce(n => n + 1);
  }, [segments]);

  const handleSegmentLoop = useCallback((segIndex: number) => {
    const seg = segments.find(sg => sg.index === segIndex);
    if (!seg) return;
    setSelectedSegment(seg.id);
    setSecondarySegment(null);
    setSegPlay(prev => (prev && prev.id === seg.id && prev.loop) ? null : { id: seg.id, loop: true });
    setSegPlayNonce(n => n + 1);
  }, [segments]);

  const handleSegmentMute = useCallback((segIndex: number) => {
    pushUndo();
    setSegments(prev => prev.map(s =>
      s.index === segIndex ? { ...s, muted: !s.muted } : s
    ));
  }, [pushUndo]);

  /**
   * Cuts one segment in two at `atTime`. Both halves must clear the minimum length, so a cut
   * within half a second of either edge is refused rather than making a sliver.
   *
   * Selection follows the playhead into whichever half now contains it — that half is what the
   * preview is showing, and what Speed, Mute and Transitions will act on next. The playhead
   * itself does not move: a split changes where the boundaries are, not the clip's timeline.
   */
  const splitSegmentAt = useCallback((segIndex: number, atTime: number) => {
    const seg = segments.find(s => s.index === segIndex);
    if (!seg) return;
    if (atTime - seg.startTime < MIN_SEGMENT_DURATION) return;
    if (seg.endTime - atTime < MIN_SEGMENT_DURATION) return;
    pushUndo();
    // The left half keeps the segment's identity; only the new tail gets a new id.
    const left: Segment = { ...seg, endTime: atTime };
    const right: Segment = {
      ...seg, startTime: atTime, id: makeSegmentId(segIdScope), index: seg.index + 1,
    };
    setSegments(prev => {
      const arr = prev.filter(s => s.index !== segIndex);
      arr.push(left, right);
      arr.sort((a, b) => a.startTime - b.startTime);
      return arr.map((s, i) => ({ ...s, index: i }));
    });
    // Whichever half holds the playhead becomes the selection.
    setSelectedSegment(currentTime >= atTime ? right.id : left.id);
    setSecondarySegment(null);
  }, [segments, currentTime, pushUndo, segIdScope]);

  /** The toolbar's Split: cuts the selected segment at the playhead. */
  const handleSegmentSplit = useCallback(() => {
    const pos = segments.findIndex(s => s.id === selectedSegment);
    if (pos < 0) return;
    splitSegmentAt(pos, currentTime);
  }, [selectedSegment, segments, currentTime, splitSegmentAt]);

  const handleSegmentDuplicate = useCallback(() => {
    const pos = segments.findIndex(s => s.id === selectedSegment);
    if (pos < 0) return;
    pushUndo();
    const dupe: Segment = { ...segments[pos], id: makeSegmentId(segIdScope) };
    setSegments(prev => {
      const arr = [...prev];
      arr.splice(pos + 1, 0, dupe);
      return arr.map((s, i) => ({ ...s, index: i }));
    });
  }, [selectedSegment, segments, pushUndo, segIdScope]);

  // Segment speed change
  const handleSegmentSpeed = useCallback((speed: number) => {
    if (selectedSegment === null) return;
    pushUndo();
    setSegments(prev => prev.map(s =>
      s.id === selectedSegment ? { ...s, speed } : s
    ));
  }, [selectedSegment, pushUndo]);

  // Segment transition change
  const handleSegmentTransition = useCallback((transition: string) => {
    if (selectedSegment === null) return;
    pushUndo();
    setSegments(prev => prev.map(s =>
      s.id === selectedSegment ? { ...s, transition } : s
    ));
  }, [selectedSegment, pushUndo]);

  // ── Caption handlers ──

  const handleCaptionsChange = useCallback((newCaptions: Caption[]) => {
    pushUndo();
    setCaptions(newCaptions);
  }, [pushUndo]);

  // ── Undo / Redo ──

  const handleUndo = useCallback(() => {
    if (undoStack.length === 0) return;
    const prev = undoStack[undoStack.length - 1];
    setRedoStack(r => [...r, getSnapshot()]);
    setUndoStack(u => u.slice(0, -1));
    setCaptions(prev.captions);
    setSegments(prev.segments);
    setAspectRatio(prev.aspectRatio);
    setMasterVolume(prev.masterVolume);
    setPlaybackSpeed(prev.playbackSpeed);
    setDirty(true);
  }, [undoStack, getSnapshot]);

  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    setUndoStack(u => [...u, getSnapshot()]);
    setRedoStack(r => r.slice(0, -1));
    setCaptions(next.captions);
    setSegments(next.segments);
    setAspectRatio(next.aspectRatio);
    setMasterVolume(next.masterVolume);
    setPlaybackSpeed(next.playbackSpeed);
    setDirty(true);
  }, [redoStack, getSnapshot]);

  // ── Keyboard shortcuts ──

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA') return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') { e.preventDefault(); handleUndo(); return; }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') { e.preventDefault(); handleRedo(); return; }
      if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); handleSave(); return; }
      if ((e.ctrlKey || e.metaKey) && (e.key === '=' || e.key === '+')) { e.preventDefault(); setZoom(z => Math.min(10, z + 1)); return; }
      if ((e.ctrlKey || e.metaKey) && e.key === '-') { e.preventDefault(); setZoom(z => Math.max(1, z - 1)); return; }
      if ((e.ctrlKey || e.metaKey) && e.key === '0') { e.preventDefault(); setZoom(1); return; }
      if (e.key === 't' || e.key === 'T') { setTrimActive(t => !t); return; }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const pos = segmentsRef.current.findIndex(sg => sg.id === selectedSegment);
        if (pos >= 0) { e.preventDefault(); handleSegmentDelete(pos); }
        return;
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleUndo, handleRedo, selectedSegment, handleSegmentDelete]);

  // Auto-save every 30 seconds
  useEffect(() => {
    if (!dirty || !clipData) return;
    const timer = setTimeout(() => handleSave(), 30000);
    return () => clearTimeout(timer);
  }, [dirty, captions, segments, aspectRatio, masterVolume, playbackSpeed]);

  // ── Save ──

  /**
   * Save first: the Story reads this clip's edit list at render time, so unsaved cuts would be
   * lost. A save that fails keeps the author here with their edits, rather than landing them
   * back on the story with the work gone — the error toast says why.
   */
  const handleDoneReturn = async () => {
    if (!returnTo) return;
    if (dirty) {
      const saved = await handleSave();
      if (!saved) return;
    }
    // Queue the render before leaving: the viewer lists versions, and an edit that was never
    // exported produces none. Failing to queue keeps the author here with the reason, rather
    // than returning them to a page that will never show what they just made.
    if (exportOnDone && clipData) {
      setSaving(true);
      try {
        const res = await fetch(`${API_BASE}/api/shorts/${clipData.id}/export`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ quality: 'hd', codec: 'h264', fps: 30 }),
        });
        if (!res.ok) {
          const payload = await res.json().catch(() => ({}));
          setError(payload?.error || 'Could not start the export');
          setSaving(false);
          return;
        }
      } catch {
        setError('Could not start the export');
        setSaving(false);
        return;
      }
      setSaving(false);
    }
    navigate(returnTo);
  };

  const handleSave = async (): Promise<boolean> => {
    if (!clipData || saving) return false;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/shorts/${clipData.id}/edit`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          editVersion: clipData.editVersion,
          title: clipData.title,
          caption: clipData.caption,
          tags: clipData.tags,
          captions: captions.map(c => ({
            id: c.id.startsWith('new-') ? null : c.id,
            text: c.text,
            startTime: c.start_time,
            endTime: c.end_time,
            style: c.style,
          })),
          segments: segments.map(s => ({
            // The id is the row key now — the server upserts on it, so identity survives a save.
            id: s.id,
            index: s.index,
            startTime: s.startTime,
            endTime: s.endTime,
            volume: s.volume,
            muted: s.muted,
            fadeIn: s.fadeIn,
            fadeOut: s.fadeOut,
            speed: s.speed,
            transition: s.transition,
          })),
          crop,
          masterVolume,
          playbackSpeed,
          aspectRatio,
          overlays: overlays.map(o => ({
            type: o.type, content: o.content,
            startTime: o.startTime, endTime: o.endTime,
            x: o.x, y: o.y, scale: o.scale,
          })),
          images: images.map(img => ({
            layer: img.layer, imageUrl: img.imageUrl,
            x: img.x, y: img.y, width: img.width, height: img.height,
            opacity: img.opacity, rotation: img.rotation,
            startTime: img.startTime, endTime: img.endTime ?? duration,
            segmentIds: img.segmentIds,
            crop: img.crop,
          })),
          filterPreset: filters.preset,
          filterBrightness: filters.brightness,
          filterContrast: filters.contrast,
          filterSaturation: filters.saturation,
          originalAudioVolume,
        }),
      });
      const data = await res.json();
      if (res.status === 409) {
        setError('Edit conflict — someone else edited this clip. Please reload.');
        return false;
      }
      if (!res.ok) throw new Error(data.error || 'Save failed');
      setClipData(prev => prev ? { ...prev, editVersion: data.editVersion } : prev);
      setDirty(false);
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  /**
   * Throws away everything since the last save by reloading the clip.
   *
   * Music, the thumbnail and uploaded overlay images are committed by their own endpoints the
   * moment they are chosen, so they survive this — what comes back is the clip as the server
   * holds it, which is the only state the render will ever see. Autosave means "the last save"
   * can be up to thirty seconds old, so this asks first.
   */
  const handleDiscard = () => {
    if (!clipData) return;
    if (dirty && !window.confirm('Discard changes since the last save? Music, thumbnail and uploaded images are already saved and will remain.')) return;
    setReloadNonce(n => n + 1);
  };

  const handleRegenerate = async () => {
    if (!clipData) return;
    await fetch(`${API_BASE}/api/shorts/${clipData.id}/captions/generate`, {
      method: 'POST',
      credentials: 'include',
    });
  };

  const handleSeek = useCallback((time: number) => {
    setCurrentTime(time);
  }, []);

  const handleAddCaption = () => {
    const newCap: Caption = {
      id: `new-${Date.now()}`,
      text: 'New caption',
      start_time: currentTime,
      end_time: Math.min(currentTime + 2, duration),
      style: {},
    };
    handleCaptionsChange([...captions, newCap]);
    // The new caption is only editable in the Caption tab — go there rather than leaving the
    // author on Tool wondering whether the click did anything.
    setRightTab('caption');
  };

  const handleAddEmoji = () => { setShowEmojiPicker(true); };

  /**
   * Draws an emoji to a transparent PNG at render resolution.
   *
   * Emoji are stored as image overlays rather than as text: the render hosts have no colour
   * emoji font FFmpeg can draw with, so an emoji kept as text renders as nothing at all. The
   * browser already has the font, so it rasterises here — once, when the emoji is added — and
   * from then on it is an ordinary image overlay, which both render paths already draw.
   */
  const emojiToPng = (emoji: string): Promise<Blob | null> => {
    const SIZE = 256;
    const canvas = document.createElement('canvas');
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return Promise.resolve(null);
    ctx.clearRect(0, 0, SIZE, SIZE);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // A little under the canvas so the tallest glyphs keep their padding.
    ctx.font = `${Math.round(SIZE * 0.8)}px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif`;
    ctx.fillText(emoji, SIZE / 2, SIZE / 2 + SIZE * 0.04);
    return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  };

  /** Fraction of the frame's height a square overlay needs to stay square in this aspect. */
  const squareHeightFor = (widthFraction: number) => {
    const [w, h] = aspectRatio.split(':').map(Number);
    const ratio = w > 0 && h > 0 ? w / h : 9 / 16;
    return widthFraction * ratio;
  };

  const handleEmojiAdd = async (overlay: Overlay) => {
    if (!clipData) return;
    const blob = await emojiToPng(overlay.content);
    if (!blob) { setError('This browser cannot rasterise emoji'); return; }
    const formData = new FormData();
    formData.append('file', new File([blob], `emoji-${Date.now()}.png`, { type: 'image/png' }));
    formData.append('layer', 'front');
    const res = await fetch(`${API_BASE}/api/shorts/${clipData.id}/image`, {
      method: 'POST', credentials: 'include', body: formData,
    });
    const data = await res.json();
    if (!res.ok) { setError(data.error || 'Could not add the emoji'); return; }
    pushUndo();
    const width = 0.2 * overlay.scale;
    setImages(prev => [...prev, {
      id: data.imageId, layer: data.layer, imageUrl: data.imageUrl,
      src: assetSrc(data.src || data.imageUrl),
      x: overlay.x, y: overlay.y,
      width, height: squareHeightFor(width),
      opacity: 1.0, rotation: 0,
      startTime: overlay.startTime, endTime: overlay.endTime, segmentIds: [],
      crop: FULL_CROP,
    }]);
    setSelectedImageId(data.imageId);
    setImagePanelId(data.imageId);
    setDirty(true);
  };
  const handleAddImage = async () => {
    if (!clipData) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/webp,image/gif';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file || file.size > 5 * 1024 * 1024) { alert('File must be under 5MB'); return; }
      const formData = new FormData();
      formData.append('file', file);
      formData.append('layer', 'front');
      const res = await fetch(`${API_BASE}/api/shorts/${clipData.id}/image`, {
        method: 'POST', credentials: 'include', body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        pushUndo();
        setImages(prev => [...prev, {
          id: data.imageId, layer: data.layer, imageUrl: data.imageUrl,
          src: assetSrc(data.src || data.imageUrl),
          x: 0.5, y: 0.5, width: 0.3, height: 0.3, opacity: 1.0, rotation: 0,
          startTime: 0, endTime: null, segmentIds: [],
          crop: FULL_CROP,
        }]);
        setSelectedImageId(data.imageId);
        setImagePanelId(data.imageId);
      }
    };
    input.click();
  };
  const handleThumbnail = () => { setShowThumbnail(true); };
  const handleFilters = () => { setShowFilters(f => !f); setShowEmojiPicker(false); setShowMusic(false); };
  const handleMusic = () => { setShowMusic(m => !m); setShowEmojiPicker(false); setShowFilters(false); };

  const handleMasterVolumeToggle = useCallback(() => {
    pushUndo();
    setMasterVolume(v => v > 0 ? 0 : 1);
  }, [pushUndo]);

  const handleContextMenu = useCallback((e: React.MouseEvent, segIndex: number, atTime: number) => {
    setContextMenu({ x: e.clientX, y: e.clientY, segIndex, atTime });
    setSelectedSegment(segments[segIndex]?.id ?? null);
  }, [segments]);

  // Selected segment data
  // The timeline, the toolbar and the context menu all address segments by position; the
  // selection itself is an id. This is the one place the two meet.
  const selectedPos = selectedSegment !== null ? segments.findIndex(s => s.id === selectedSegment) : -1;
  const secondaryPos = secondarySegment !== null ? segments.findIndex(s => s.id === secondarySegment) : -1;
  const selectedSeg = selectedPos >= 0 ? segments[selectedPos] : undefined;

  const playSeg = segPlay ? segments.find(s => s.id === segPlay.id) : undefined;
  const playRange = segPlay && playSeg
    ? { start: playSeg.startTime, end: playSeg.endTime, loop: segPlay.loop }
    : null;

  if (loading) {
    return (
      <div className="app-layout">
        <Sidebar />
        <main className="main-content">
          <div className="clip-editor-loading">Loading clip editor...</div>
        </main>
      </div>
    );
  }

  if (error && !clipData) {
    return (
      <div className="app-layout">
        <Sidebar />
        <main className="main-content">
          <div className="clip-editor-error">
            <p>{error}</p>
            <button className="btn-primary" onClick={() => window.location.reload()}>Retry</button>
          </div>
        </main>
      </div>
    );
  }

  if (!clipData) return null;

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content clip-editor-layout">
        {/* Top bar — spans the full width beside the app sidebar */}
        <div className="clip-editor-top-bar">
            {storyCtx ? (
              <div className="breadcrumb breadcrumb-inline">
                <div className="breadcrumb-links">
                  <BreadcrumbBack to={returnTo || (storyCtx.projectId ? `/projects/${storyCtx.projectId}` : '/projects')} />
                  <Link to="/projects">Projects</Link>
                  <span className="breadcrumb-sep">›</span>
                  {storyCtx.projectId
                    ? <Link to={`/projects/${storyCtx.projectId}`}>{storyCtx.projectName}</Link>
                    : <span>{storyCtx.projectName}</span>}
                  <span className="breadcrumb-sep">›</span>
                  {returnTo ? <Link to={returnTo}>{storyCtx.name}</Link> : <span>{storyCtx.name}</span>}
                  <span className="breadcrumb-sep">›</span>
                  <span>Slide {slideParam != null ? Number(slideParam) + 1 : ''}</span>
                </div>
              </div>
            ) : returnTo && returnLabel ? (
              /* Sent here from a renders page: name it, the way a Story names itself. */
              <div className="breadcrumb breadcrumb-inline">
                <div className="breadcrumb-links">
                  <BreadcrumbBack to={returnTo} />
                  <Link to="/projects">Projects</Link>
                  <span className="breadcrumb-sep">›</span>
                  <Link to={returnTo}>{returnLabel}</Link>
                  <span className="breadcrumb-sep">›</span>
                  <span>{clipData.title || `Clip #${clipData.index + 1}`}</span>
                </div>
              </div>
            ) : (
              <h2>{clipData.title || `Clip #${clipData.index + 1}`}</h2>
            )}
            <div className="clip-editor-top-actions">
              {dirty && <span className="unsaved-badge">Unsaved</span>}
              <button
                className="header-action-btn"
                onClick={handleDiscard}
                disabled={saving}
                title="Reload the clip as it was last saved, discarding changes since"
              >
                Discard
              </button>
              <button className="header-action-btn header-action-primary" onClick={() => setShowExport(true)} disabled={saving}>
                Export
              </button>
              <button className="header-action-btn" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              {returnTo && (
                <button
                  className="header-action-btn"
                  onClick={handleDoneReturn}
                  disabled={saving}
                  title={
                    exportOnDone ? `Save, export this version, and go back${returnLabel ? ` to ${returnLabel}` : ''}`
                      : storyCtx ? `Save and return to ${storyCtx.name}` : 'Save and return to the story'
                  }
                >
                  {saving
                    ? 'Saving…'
                    : exportOnDone
                      ? 'Done → Export'
                      : `Done → ${storyCtx ? `${storyCtx.name} Story` : 'Story'}`}
                </button>
              )}
            </div>
        </div>

        {/* Phones: Tool | Caption live in a left-hand drawer, opened from here. */}
        <div className="clip-mobile-bar">
          <span className="clip-mobile-label">&#9986; Clip editor</span>
          <button
            type="button"
            className="clip-rail-btn"
            onClick={() => setRailOpen(true)}
            title="Tools and captions"
            aria-label="Tools and captions"
            aria-expanded={railOpen}
          >
            <RailIcon aria-hidden={true} />
          </button>
        </div>

        {/* Center — editing toolbar + video preview */}
        <div className="clip-editor-center">
          <TimelineToolbar
            onUndo={handleUndo}
            onRedo={handleRedo}
            canUndo={undoStack.length > 0}
            canRedo={redoStack.length > 0}
            onCrop={() => { setCropMode(c => !c); }}
            onResetSegments={handleResetSegments}
            masterVolume={masterVolume}
            onMasterVolumeToggle={handleMasterVolumeToggle}
            playbackSpeed={playbackSpeed}
            onPlaybackSpeedChange={(sp) => { pushUndo(); setPlaybackSpeed(sp); setDirty(true); }}
          />
          <div className="clip-sticky-player">
          <VideoPreview
            fileUrl={clipData.fileUrl}
            previewIsSource={clipData.previewIsSource}
            clipStart={clipData.startTime}
            clipEnd={clipData.endTime}
            captions={captions}
            currentTime={currentTime}
            onTimeUpdate={setCurrentTime}
            onDurationChange={setDuration}
            aspectRatio={aspectRatio}
            filterCSS={buildFilterCSS(filters)}
            crop={crop}
            overlays={overlays}
            images={images}
            selectedOverlayId={selectedOverlayId}
            onOverlaySelect={setSelectedOverlayId}
            onOverlayMove={(id, x, y) => { pushUndo(); setOverlays(prev => prev.map(o => o.id === id ? { ...o, x, y } : o)); setDirty(true); }}
            onOverlayScale={(id, scale) => { pushUndo(); setOverlays(prev => prev.map(o => o.id === id ? { ...o, scale } : o)); setDirty(true); }}
            onOverlayDelete={(id) => { pushUndo(); setOverlays(prev => prev.filter(o => o.id !== id)); setSelectedOverlayId(null); setDirty(true); }}
            selectedImageId={selectedImageId}
            onImageSelect={(id) => { setSelectedImageId(id); if (id) setImagePanelId(id); }}
            onImageMove={(id, x, y) => { pushUndo(); setImages(prev => prev.map(i => i.id === id ? { ...i, x, y } : i)); setDirty(true); }}
            onImageResize={(id, w, h, x, y) => { pushUndoOnce(); setImages(prev => prev.map(i => i.id === id ? { ...i, width: w, height: h, x, y } : i)); setDirty(true); }}
            croppingImageId={croppingImageId}
            onImageCrop={(id, crop) => { pushUndo(); setImages(prev => prev.map(i => i.id === id ? { ...i, crop } : i)); setDirty(true); }}
            onImageCropDone={() => setCroppingImageId(null)}
            onImageRotate={(id, r) => { pushUndo(); setImages(prev => prev.map(i => i.id === id ? { ...i, rotation: r } : i)); setDirty(true); }}
            onImageDelete={(id) => { pushUndo(); setImages(prev => prev.filter(i => i.id !== id)); setSelectedImageId(null); setImagePanelId(prev => prev === id ? null : prev); setCroppingImageId(prev => prev === id ? null : prev); setDirty(true); }}
            cropping={cropMode}
            segments={segments}
            masterVolume={masterVolume}
            playbackSpeed={playbackSpeed}
            playRange={playRange}
            playNonce={segPlayNonce}
            onPlayRangeEnd={() => setSegPlay(null)}
          >
            {cropMode && (
              <CropOverlay
                crop={crop}
                onApply={(newCrop) => { pushUndo(); setCrop(newCrop); setCropMode(false); setDirty(true); }}
                onCancel={() => setCropMode(false)}
              />
            )}
          </VideoPreview>
          {/* Phones: pinned under the video so the caption at the playhead stays in view. */}
          <div className="clip-now-caption">
            {nowCaption ? (
              <>
                <span className="clip-now-time">{nowCaption.start_time.toFixed(1)}&#8211;{nowCaption.end_time.toFixed(1)}</span>
                <span className="clip-now-text">{nowCaption.text}</span>
              </>
            ) : (
              <span className="clip-now-empty">No captions yet</span>
            )}
          </div>
          </div>
          <SegmentToolbar
            hasSelection={selectedPos >= 0}
            segmentNumber={selectedPos >= 0 ? selectedPos + 1 : null}
            segmentCount={segments.length}
            trimActive={trimActive}
            onTrimToggle={() => setTrimActive(t => !t)}
            onSplit={handleSegmentSplit}
            onMoveLeft={() => { if (selectedPos >= 0) handleSegmentMove(selectedPos, -1); }}
            onMoveRight={() => { if (selectedPos >= 0) handleSegmentMove(selectedPos, 1); }}
            canMoveLeft={selectedPos > 0}
            canMoveRight={selectedPos >= 0 && selectedPos < segments.length - 1}
            onMerge={handleSegmentMerge}
            canMerge={selectedPos >= 0 && secondaryPos >= 0 && Math.abs(selectedPos - secondaryPos) === 1}
            onDuplicate={handleSegmentDuplicate}
            onDelete={() => { if (selectedPos >= 0) handleSegmentDelete(selectedPos); }}
            canDelete={segments.length > 1}
            muted={selectedSeg?.muted ?? false}
            onMuteToggle={() => { if (selectedPos >= 0) handleSegmentMute(selectedPos); }}
            volume={selectedSeg?.volume ?? masterVolume}
            onVolumeChange={(v) => {
              if (selectedSegment === null) return;
              pushUndo();
              setSegments(prev => prev.map(sg =>
                sg.id === selectedSegment ? { ...sg, volume: v } : sg
              ));
            }}
            speed={selectedSeg?.speed ?? playbackSpeed}
            onSpeedChange={handleSegmentSpeed}
            transition={selectedSeg?.transition ?? 'none'}
            onTransitionChange={handleSegmentTransition}
            zoom={zoom}
            onZoomChange={setZoom}
          />
        </div>

        {/* Right rail — Tool | Caption */}
        <div className={`clip-rail-backdrop${railOpen ? ' is-open' : ''}`} onClick={() => setRailOpen(false)} aria-hidden="true" />
        <div className={`clip-right-panel${railOpen ? ' clip-right-panel--open' : ''}`}>
          <div className="clip-rail-head">
            <span className="clip-rail-title">Tools &amp; captions</span>
            <button type="button" className="clip-rail-close" onClick={() => setRailOpen(false)} aria-label="Close tools and captions">&#10005;</button>
          </div>
          <div className="clip-right-tabs">
            <button
              className={`clip-right-tab ${rightTab === 'tool' ? 'clip-right-tab--active' : ''}`}
              onClick={() => setRightTab('tool')}
              title="Editing tools — aspect, captions, emoji, images, filters, speed, music"
            >
              Tool
            </button>
            <button
              className={`clip-right-tab ${rightTab === 'caption' ? 'clip-right-tab--active' : ''}`}
              onClick={() => setRightTab('caption')}
              title="Caption list and styling for this clip"
            >
              Caption
            </button>
          </div>
          <div className="clip-right-body">
            {rightTab === 'tool' ? (
              <div className="clip-tool-pane">
                <Toolbar
                  aspectRatio={aspectRatio}
                  onAspectChange={(ar) => { pushUndo(); setAspectRatio(ar); }}
                  onAddCaption={handleAddCaption}
                  onAddEmoji={handleAddEmoji}
                  onAddImage={handleAddImage}
                  onThumbnail={handleThumbnail}
                  onFilters={handleFilters}
                  onMusic={handleMusic}
                  onExport={() => setShowExport(true)}
                />
              </div>
            ) : (
              <CaptionPanel
                captions={captions}
                onCaptionsChange={handleCaptionsChange}
                onRegenerate={handleRegenerate}
                currentTime={currentTime}
              />
            )}
          </div>
        </div>

        {/* Bottom Panel — Timeline Toolbar + Timeline + Editing Toolbar */}
        <div className="clip-editor-bottom">
          <Timeline
            segments={segments}
            trimActive={trimActive}
            duration={duration}
            currentTime={currentTime}
            zoom={zoom}
            onSeek={handleSeek}
            onSegmentSelect={handleSegmentSelect}
            selectedSegment={selectedPos >= 0 ? selectedPos : null}
            secondarySegment={secondaryPos >= 0 ? secondaryPos : null}
            onSegmentTrim={handleSegmentTrim}
            onSegmentTrimStart={pushUndo}
            onSegmentReorder={handleSegmentReorder}
            onSegmentDelete={handleSegmentDelete}
            onSegmentMute={handleSegmentMute}
            playingSegment={playSeg && segPlay && !segPlay.loop ? playSeg.index : null}
            loopingSegment={playSeg && segPlay && segPlay.loop ? playSeg.index : null}
            onSegmentPlay={handleSegmentPlay}
            onSegmentLoop={handleSegmentLoop}
            onContextMenu={handleContextMenu}
          />
        </div>

        {/* Adjust Panel Flyout */}
        {showAdjust && selectedSeg && (
          <AdjustPanel
            volume={(selectedSeg.volume ?? masterVolume) * 100}
            onVolumeChange={(v) => {
              pushUndo();
              setSegments(prev => prev.map(s =>
                s.id === selectedSegment ? { ...s, volume: v / 100 } : s
              ));
            }}
            speed={selectedSeg.speed ?? playbackSpeed}
            onSpeedChange={handleSegmentSpeed}
            brightness={filters.brightness}
            onBrightnessChange={(v) => { pushUndo(); setFilters(f => ({ ...f, brightness: v })); setDirty(true); }}
            contrast={filters.contrast}
            onContrastChange={(v) => { pushUndo(); setFilters(f => ({ ...f, contrast: v })); setDirty(true); }}
            saturation={filters.saturation}
            onSaturationChange={(v) => { pushUndo(); setFilters(f => ({ ...f, saturation: v })); setDirty(true); }}
            onClose={() => setShowAdjust(false)}
          />
        )}

        {/* Segment Context Menu */}
        {contextMenu && (
          <SegmentContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            segmentIndex={contextMenu.segIndex}
            isMuted={segments.find(s => s.index === contextMenu.segIndex)?.muted ?? false}
            // Split Here cuts where the pointer was, not where the playhead is.
            onSplit={() => splitSegmentAt(contextMenu.segIndex, contextMenu.atTime)}
            onMoveLeft={() => handleSegmentMove(contextMenu.segIndex, -1)}
            onMoveRight={() => handleSegmentMove(contextMenu.segIndex, 1)}
            canMoveLeft={contextMenu.segIndex > 0}
            canMoveRight={contextMenu.segIndex < segments.length - 1}
            onDuplicate={handleSegmentDuplicate}
            onDelete={() => handleSegmentDelete(contextMenu.segIndex)}
            onMute={() => handleSegmentMute(contextMenu.segIndex)}
            onVolumeOpen={() => { setShowAdjust(true); setSelectedSegment(segments[contextMenu.segIndex]?.id ?? null); }}
            onExportSegment={() => {
              const seg = segments.find(s => s.index === contextMenu.segIndex);
              if (seg) {
                setExportSegment({ start: seg.startTime, end: seg.endTime, label: `Segment ${seg.index + 1}` });
                setShowExport(true);
              }
            }}
            onAddCaption={handleAddCaption}
            onAddEmoji={handleAddEmoji}
            onClose={() => setContextMenu(null)}
          />
        )}

        {/* Export Modal */}
        {showExport && (
          <ExportModal
            shortId={clipData.id}
            aspectRatio={aspectRatio}
            dirty={dirty}
            onClose={() => { setShowExport(false); setExportSegment(null); }}
            segmentStart={exportSegment?.start}
            segmentEnd={exportSegment?.end}
            segmentLabel={exportSegment?.label}
          />
        )}

        {/* Emoji Picker Panel */}
        {showEmojiPicker && (
          <EmojiPicker
            currentTime={currentTime}
            duration={duration}
            onAdd={handleEmojiAdd}
            onClose={() => setShowEmojiPicker(false)}
            selectedOverlay={selectedOverlayId ? overlays.find(o => o.id === selectedOverlayId) : null}
            onUpdate={(updated) => { pushUndo(); setOverlays(prev => prev.map(o => o.id === updated.id ? updated : o)); setDirty(true); }}
            onDelete={(id) => { pushUndo(); setOverlays(prev => prev.filter(o => o.id !== id)); setSelectedOverlayId(null); setDirty(true); }}
          />
        )}

        {/* Image Overlay Panel */}
        {imagePanelId && images.find(i => i.id === imagePanelId) && (
          <ImageOverlayPanel
            image={images.find(i => i.id === imagePanelId)!}
            duration={duration}
            segments={segments}
            onUpdate={(updated) => { pushUndo(); setImages(prev => prev.map(i => i.id === updated.id ? updated : i)); setDirty(true); }}
            onDelete={(id) => { pushUndo(); setImages(prev => prev.filter(i => i.id !== id)); setSelectedImageId(null); setImagePanelId(null); setCroppingImageId(null); setDirty(true); }}
            cropping={croppingImageId === imagePanelId}
            onToggleCropOnFrame={() => {
              setCroppingImageId(prev => (prev === imagePanelId ? null : imagePanelId));
              setSelectedImageId(imagePanelId);
            }}
            onClose={() => { setImagePanelId(null); setCroppingImageId(null); }}
          />
        )}

        {/* Thumbnail Picker */}
        {showThumbnail && (
          <ThumbnailPicker
            shortId={clipData.id}
            thumbnailFrames={clipData.thumbnailFrames}
            currentTime={currentTime}
            currentThumbnail={clipData.thumbnailUrl || undefined}
            onSelect={(url) => { setClipData(prev => prev ? { ...prev, thumbnailUrl: url } : prev); setShowThumbnail(false); }}
            onClose={() => setShowThumbnail(false)}
          />
        )}

        {/* Filters Panel */}
        {showFilters && (
          <FiltersPanel
            filters={filters}
            onChange={(f) => { pushUndo(); setFilters(f); setDirty(true); }}
            onClose={() => setShowFilters(false)}
          />
        )}

        {/* Music Panel */}
        {showMusic && (
          <MusicPanel
            shortId={clipData.id}
            music={music}
            originalAudioVolume={originalAudioVolume}
            onMusicSet={(m) => { setMusic(m); setDirty(true); }}
            onMusicRemove={() => { setMusic(null); setDirty(true); }}
            onMusicUpdate={(m) => { setMusic(m); setDirty(true); }}
            onOriginalVolumeChange={(v) => { pushUndo(); setOriginalAudioVolume(v); setDirty(true); }}
            onClose={() => setShowMusic(false)}
          />
        )}

        {/* Toast for errors */}
        {error && clipData && (
          <div className="clip-editor-toast">
            <span>{error}</span>
            <button className="btn-icon" onClick={() => setError(null)}>✕</button>
          </div>
        )}
      </main>
    </div>
  );
};

export default ClipEditor;
